import React, { useState, useMemo, useRef, useEffect } from 'react';
import { TASK_STATUS_ICONS, TASK_STATUS_ICON_COLORS } from '../../constants/taskStatuses';
import useDragScroll from '../../hooks/useDragScroll';
import useGanttScrollFrame from '../../hooks/useGanttScrollFrame';
import useGanttZoom from '../../hooks/useGanttZoom';
import GanttDateAxis, { GANTT_AXIS_Z, GANTT_BAR_HEIGHT, GANTT_HEADER_HEIGHT, GANTT_PANEL_Z, GanttGrid, GanttTodayLine } from '../gantt/GanttDateAxis';
import useMeasuredHeight from '../../hooks/useMeasuredHeight';
import { scrollUnderPinnedChrome } from '../../hooks/useOpenAtTop';
import StickyScrollbar from '../ui/StickyScrollbar';
import { PAGE_SCROLL_PANE_X } from '../../constants/stickyTable';
import { parseDate, daysDiff, addDays, toMonday, buildDateHeaders } from '../../utils/ganttDates';
import { formatDate } from '../../utils/dateFormat';
import { MILESTONE_DEFS } from '../gags/GagGanttView';
import GanttZoomControl from '../gantt/GanttZoomControl';
import GanttMilestoneLegend, { useMilestoneVisibility, NO_HIDDEN_MILESTONES } from '../gantt/GanttMilestoneLegend';
import { MilestoneShape, MILESTONE_SHAPE_CLASSES, fadedBarFill, unassignedBarStyle, withHexAlpha } from './CrossTeamGanttView';

const TEAM_COLORS = ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#F43F5E', '#06B6D4', '#F97316', '#6366F1'];

// The shared Gantt bar height (22 since 2026-10-13, with the Timeline; the
// label is the same `text-xs`, so the two charts still match).
const BAR_HEIGHT = GANTT_BAR_HEIGHT;
const BAR_GAP = 2;
// An OWNER SPAN is a summary rail, not a bar (owner 2026-10-13: one per gag a
// person OWNS, "to signify the whole job"; the old team-lead span's stripes and
// dashed border read as "less valid"). A slim solid bracket in the gag's colour
// over the person's tasks, its label above it; shorter than a task lane.
// Label line (12), then the rail; the gag's milestone markers (12px) sit
// CENTRED on the rail's bar, in the 4px above it and the legs' depth below.
const LEAD_LANE_HEIGHT = 24;
const LEAD_LABEL_HEIGHT = 12;
const LEAD_RAIL_HEIGHT = 8;
const LEAD_RAIL_BAR = 4;
const MILESTONE_SIZE = 12;
const BLOCK_GAP = 6; // between one owned gag's block and whatever is under it
const ROW_PADDING = 6;
const LABEL_WIDTH = 180;

// Colour key for a task bar: team tasks colour by team; gag-only tasks
// (team_id null) colour by gag, so a person's gag work is legible and doesn't
// all collapse into one "no team" colour. Team tasks always win when both set.
function colorKey(t) {
  if (t.team_id != null) return `team-${t.team_id}`;
  if (t.gag_id != null) return `gag-${t.gag_id}`;
  return 'none';
}
function colorLabel(t) {
  if (t.team_id != null) return t.team_name || `Team ${t.team_id}`;
  if (t.gag_id != null) return t.gag_title || `Gag ${t.gag_id}`;
  return t.personal ? PERSONAL_LABEL : 'Unassigned';
}

/* PERSONAL (no-gag) TASKS — owner, 2026-10-12: *"show them in a differnt colour
   (maybe pale or grey) So its easy to see if that person is overloaded or even
   inflating their work load"*. The server flags them (`personal`); the bar is a
   pale grey with dark text, set INLINE so index.css's dark-mode `!important`
   colour table cannot repaint it. Colour is not the only signal: the tooltip's
   second line says what it is (the legend is gone — see below). */
export const PERSONAL_LABEL = 'Personal task (no gag)';
export const PERSONAL_BAR = { fill: '#D1D5DB', border: '#9CA3AF', text: '#1F2937' };
// The row for gags nobody owns. A string id, so it can never collide with a user's.
export const UNOWNED_ROW_ID = 'unowned';
export const UNOWNED_LABEL = 'Unowned gags';

/* 📜 ONE PAGE SCROLL (2026-10-05, owner: *"check all gannt charts for all the
   updates we are making. should be common code/fixes where possible"*). This
   chart never had an inner vertical scroller, but its month/day header scrolled
   away with the rows and its native horizontal bar sat at the chart's bottom.
   Now, as the Crew Gantt: the header rows live in a band OUTSIDE the x-only
   timeline, pinned to the page at `stickyTop` (the Breakdown's MEASURED
   `useStickyToolbar().headerTop`); the timeline hides its own bar for a
   `ui/StickyScrollbar` while it overflows; the `ui/edgeFade` mask marks a
   clipped edge — all through `hooks/useGanttScrollFrame`. The band holds the
   shared single-row `GanttDateAxis` over `buildDateHeaders` since 2026-10-13
   (owner: the Timeline's date layout and grid); its own month band and
   week-zoom day row are gone. Desktop only — the Breakdown forces Table on a phone.
   🪤 The root is `overflow-clip`, never `overflow-hidden` (that would capture
   both stickies), and `isolate` so the band's z-30 wins inside the chart only. */
/* 📂 THE INLINE PANEL UNDER A ROW (owner 2026-10-14: a task click opens the gag
   panel "below the persons name, not under the whole chart"). The page passes
   the finished `panel` node and the `panelRowId` it belongs under; this chart
   only makes room for it. ONE horizontal scroller is kept (zoom, drag, the date
   band and the sticky bar all hang off it), so the chart is not literally cut:
   both columns get a GAP of the panel's measured height under that row, and the
   panel is laid over the gap at full card width, outside the sideways scroll —
   the Crew Gantt's level-2 panel device (`budget/CrewGanttView.js`), same tier
   (`GANTT_PANEL_Z`): over the bars and today line, under the pinned date band.
   `onTaskClick(task, rowId)` — the row the click came from, so a task shared by
   two people opens under the bar that was clicked. */
export default function LabourGanttView({ people, teamColorMap, ownerSpans, ownedGagTasks, onTaskClick, stickyTop = 0, panel = null, panelRowId = null, viewerUserId = null }) {
  const scrollRef = useRef(null);
  const [panelRef, panelMeasuredHeight] = useMeasuredHeight();
  const panelOpen = !!panel && panelRowId != null;
  const panelGap = panelOpen ? (panelMeasuredHeight || 0) : 0;
  const labelsRef = useRef(null);
  // Zoom state, pixels per day and the keep-the-centre rule (shared).
  const { zoom, dayWidth, zoomTo, applyCentre } = useGanttZoom(scrollRef);
  const bodyRef = useRef(null);
  // The timeline pans sideways; a vertical drag on the chart scrolls the WINDOW.
  useDragScroll(scrollRef, { axis: 'x' });
  useDragScroll(bodyRef, { axis: 'y', verticalPansWindow: true });
  const {
    stickyBarRef, overflows: timelineOverflows, scrollWidth: timelineScrollWidth,
    fadeStyle: timelineFadeStyle, readFade: readTimelineFade,
    dateBandRef, dateAxisClipRef, handleBandWheel,
  } = useGanttScrollFrame(scrollRef);

  const { isHidden: isMsHidden, toggle: toggleMs } = useMilestoneVisibility(NO_HIDDEN_MILESTONES);

  // Collect all tasks across all people for timeline range
  const allTasks = useMemo(() => {
    const tasks = [];
    for (const p of people) {
      for (const t of p.tasks) {
        if (t.start_date || t.due_date) tasks.push(t);
      }
    }
    return tasks;
  }, [people]);

  const spans = useMemo(() => ownerSpans || [], [ownerSpans]);

  // Owner spans grouped by person — each becomes a rail over their tasks.
  const ownerSpansByUser = useMemo(() => {
    const m = {};
    for (const s of spans) {
      if (!s.span_start && !s.span_end) continue;
      // A gag NOBODY owns arrives with no user: it goes on the Unowned row.
      const key = s.user_id == null ? UNOWNED_ROW_ID : s.user_id;
      if (!m[key]) m[key] = [];
      m[key].push(s);
    }
    return m;
  }, [spans]);

  // Build colour map from task data if not provided (keyed by colorKey()).
  // Team-keyed entries prefer the task's REAL team_color (server join) so a
  // teamed person's bars match their team's colour — Pete: team colour drives task bars. Gag-only
  // keys (no team) keep the auto-assigned palette so a person's gag work stays
  // legible instead of collapsing into one "no team" colour.
  const colors = useMemo(() => {
    if (teamColorMap) return teamColorMap;
    const map = {};
    // Owned gags join AFTER the task keys, so a gag's rail and its task bars
    // share one colour and adding the rails moved no existing bar's colour.
    const keys = [...new Set([
      ...allTasks.filter((t) => !t.personal).map(colorKey),
      ...spans.map((s) => `gag-${s.gag_id}`),
    ])];
    keys.forEach((k, i) => { map[k] = TEAM_COLORS[i % TEAM_COLORS.length]; });
    for (const t of allTasks) {
      if (t.team_id != null && t.team_color) map[`team-${t.team_id}`] = t.team_color;
    }
    return map;
  }, [allTasks, spans, teamColorMap]);

  // Timeline range — spans both assigned task bars and owner spans.
  const { timelineStart, totalDays } = useMemo(() => {
    const ranges = [];
    for (const t of allTasks) {
      ranges.push([parseDate(t.start_date) || parseDate(t.due_date), parseDate(t.due_date) || parseDate(t.start_date)]);
    }
    for (const s of spans) {
      const st = parseDate(s.span_start) || parseDate(s.span_end);
      const en = parseDate(s.span_end) || parseDate(s.span_start);
      if (st && en) ranges.push([st, en]);
    }
    if (ranges.length === 0) {
      const now = new Date();
      const start = toMonday(addDays(now, -7));
      return { timelineStart: start, totalDays: 28 };
    }
    let minDate = null, maxDate = null;
    for (const [s, e] of ranges) {
      if (s && (!minDate || s < minDate)) minDate = s;
      if (e && (!maxDate || e > maxDate)) maxDate = e;
    }
    const start = toMonday(addDays(minDate, -7));
    const end = addDays(maxDate, 14);
    return { timelineStart: start, totalDays: daysDiff(start, end) };
  }, [allTasks, spans]);

  // Scroll to today (first paint, new data range); a zoom change keeps the centre.
  useEffect(() => {
    if (applyCentre()) return;
    if (scrollRef.current) {
      const offset = daysDiff(timelineStart, new Date()) * dayWidth;
      scrollRef.current.scrollLeft = Math.max(0, offset - 200);
    }
  }, [timelineStart, dayWidth, applyCentre]);

  // THE shared date axis cells (week: a cell a day; month: a cell a week;
  // quarter: a cell a month), which also draw the grid behind the rows — the
  // Breakdown Timeline's own (owner 2026-10-13: "why has the labour view not
  // got the same grid markings and date layout as timeline?").
  const dateHeaders = useMemo(() => buildDateHeaders(zoom, timelineStart, totalDays), [zoom, timelineStart, totalDays]);

  // Compute row heights (stacked bars for overlapping tasks). An owned gag is
  // a block at the TOP of the row: its rail, then the person's tasks on it.
  // An owned gag's tasks its owner is NOT on (owner 2026-10-13), from the
  // server (`ownedGagTasks`): held by somebody else, or by nobody. Drawn faded
  // under the owner's rail; an unassigned one carries a red border.
  const tasksByGag = useMemo(() => {
    const m = {};
    for (const t of ownedGagTasks || []) (m[t.gag_id] || (m[t.gag_id] = [])).push(t);
    return m;
  }, [ownedGagTasks]);

  const personRows = useMemo(() => {
    // THE UNOWNED ROW (owner 2026-10-13: show gags not assigned to anyone, display
    // only): one extra row at the BOTTOM ("lets put the unowned gags at the
    // bottom", same day; it opened at the top), only while a gag has no owner, holding a
    // rail per unowned gag with its tasks beneath, drawn as an owner's are. It is
    // not a person: no workload, no overlap warning, no avatar.
    const rows = ownerSpansByUser[UNOWNED_ROW_ID]
      ? [...people, { user_id: UNOWNED_ROW_ID, display_name: UNOWNED_LABEL, tasks: [], overlap: false, unowned: true }]
      : people;
    return rows.map(p => {
      const datedTasks = p.tasks.filter(t => t.start_date || t.due_date);
      const sorted = [...datedTasks].sort((a, b) => (a.start_date || a.due_date || '').localeCompare(b.start_date || b.due_date || ''));
      // Simple stacking: lanes so no two bars of one group overlap. Returns the lane count.
      const stack = (tasks) => {
        const lanes = [];
        for (const task of tasks) {
          const start = parseDate(task.start_date) || parseDate(task.due_date);
          const end = parseDate(task.due_date) || parseDate(task.start_date);
          let lane = 0;
          while (lane < lanes.length && !(start > lanes[lane])) lane++;
          task._lane = lane;
          lanes[lane] = end;
        }
        return lanes.length;
      };
      // An OWNED gag is a BLOCK (owner 2026-10-13: "tasks for that gag should be
      // under the gag thin bar not mixed with other gags"): its rail, then this
      // person's own tasks on that gag. Blocks that do not overlap in time share
      // a band, so an owner of many gags does not get a row per gag.
      const bands = []; // { end, height }
      const mySpans = (ownerSpansByUser[p.user_id] || [])
        .map((s) => ({ ...s, _start: s.span_start || s.span_end, _end: s.span_end || s.span_start }))
        .sort((a, b) => a._start.localeCompare(b._start))
        .map((s) => {
          const tasks = sorted.filter((t) => t.gag_id === s.gag_id);
          // A gag has ONE owner and this span is theirs, so every listed task is this row's.
          const delegated = (tasksByGag[s.gag_id] || [])
            .map((t) => ({ ...t, _assignees: t.assignees.map((a) => a.display_name) }));
          // Under a rail the tasks read top to bottom in START DATE order, own
          // and delegated together, one per line (owner 2026-10-13). No lane
          // packing here: a packed lane would put a later task above an earlier one.
          const startOf = (t) => t.start_date || t.due_date;
          const endOf = (t) => t.due_date || t.start_date;
          [...tasks, ...delegated]
            .sort((a, b) => startOf(a).localeCompare(startOf(b)) || endOf(a).localeCompare(endOf(b)) || a.id - b.id)
            .forEach((t, i) => { t._lane = i; });
          const blockHeight = LEAD_LANE_HEIGHT + BAR_GAP + (tasks.length + delegated.length) * (BAR_HEIGHT + BAR_GAP);
          let band = 0;
          while (band < bands.length && s._start <= bands[band].end) band++;
          bands[band] = { end: s._end, height: Math.max(bands[band]?.height || 0, blockHeight) };
          return { ...s, _band: band, _tasks: tasks, _delegated: delegated };
        });
      const bandTops = [];
      let blocksHeight = 0;
      for (const b of bands) { bandTops.push(blocksHeight); blocksHeight += b.height + BLOCK_GAP; }
      for (const s of mySpans) {
        s._top = bandTops[s._band];
        for (const t of [...s._tasks, ...s._delegated]) t._top = s._top + LEAD_LANE_HEIGHT + BAR_GAP + t._lane * (BAR_HEIGHT + BAR_GAP);
      }
      // Everything else (gags owned by somebody else, team and personal tasks)
      // stacks below the blocks, as before.
      const ownedGagIds = new Set(mySpans.map((s) => s.gag_id));
      const rest = sorted.filter((t) => !ownedGagIds.has(t.gag_id));
      // A row with nothing but owned-gag blocks needs no empty task lane under them.
      const restLaneCount = Math.max(bands.length ? 0 : 1, stack(rest));
      for (const t of rest) t._top = blocksHeight + t._lane * (BAR_HEIGHT + BAR_GAP);
      const bodyHeight = blocksHeight + restLaneCount * (BAR_HEIGHT + BAR_GAP);
      const height = ROW_PADDING * 2 + bodyHeight - (restLaneCount ? BAR_GAP : BLOCK_GAP);
      // Overlap is a workload warning about a person's OWN assignments; the
      // server sets it, and the rail is not work, so it never counts.
      const hasOverlap = p.overlap != null ? p.overlap : rest.some((t) => t._lane > 0);
      return { ...p, datedTasks: sorted, mySpans, height, hasOverlap };
    });
  }, [people, ownerSpansByUser, tasksByGag]);

  const todayOffset = daysDiff(timelineStart, new Date());

  // Where the panel sits: under its row, i.e. the summed heights of every row
  // down to and including it (the rows container is the panel's offset parent).
  const panelTop = useMemo(() => {
    if (!panelOpen) return null;
    let top = 0;
    for (const p of personRows) {
      top += p.height;
      if (p.user_id === panelRowId) return top;
    }
    return null; // the row is not shown (a search narrowed it away)
  }, [panelOpen, personRows, panelRowId]);

  // Opening under a row brings that row to the top, just under the pinned
  // chrome (the date band included), so the panel below it is in view.
  useEffect(() => {
    if (panelRowId == null || !labelsRef.current) return;
    const row = labelsRef.current.querySelector(`[data-labour-row="${panelRowId}"]`);
    scrollUnderPinnedChrome(row);
  }, [panelRowId]);

  /**
   * 🔻 **THE LEGEND WAS DELETED HERE ON 2026-08-08.** Product owner: *"the
   * legend at the top of the labour view is not needed, the tooltip should tell
   * me what i need to know when i hover over a bar"*.
   *
   * 🔑 **It was safe to remove because it was DUPLICATION, not the only link
   * between colour and gag — checked before deleting, because if it had been the
   * only link this would have made the chart unreadable.** Every task bar
   * already carries `title={`${t.title}\n${colorLabel(t)}\n…`}`, and
   * `colorLabel` is the *same function* the legend used to build its entries —
   * `t.gag_title` for a gag-only task, `t.team_name` for a team task. So
   * hovering any bar names its colour group in the identical words the legend
   * used, per bar, without the user having to match a swatch by eye. Wide bars
   * additionally print `t.title` inline.
   *
   * The span key went with it for the same reason: an owner rail's own
   * `title` reads `Gag owner: {gag}` and it prints `★ {gag}` inline when it
   * is wider than 60px, so the rail explains itself on contact.
   *
   * ⚠️ **If a future bar type is added with NO `title`, this reasoning expires**
   * — the legend is not there to fall back on any more, and a colour with
   * nothing naming it is unreadable. Give the bar a tooltip, not a legend.
   */

  if (people.length === 0) {
    return <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400 italic">No team members with task assignments.</div>;
  }

  return (
    <div className="isolate border border-gray-200 dark:border-gray-700 rounded-lg overflow-clip">
      {/* Zoom. The LEGEND that used to sit at `ml-auto` on this row is gone
          (2026-08-08) — see the note above `todayOffset` for why the bars'
          own tooltips make it redundant rather than merely noisy. */}
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-700/30 border-b border-gray-200 dark:border-gray-700 flex-wrap">
        <GanttZoomControl zoom={zoom} onZoom={zoomTo} />
        {/* The gag-milestone switches (shared with the Timeline): which markers
            the owner rails carry, all four ON to start (owner 2026-10-13). Not the colour legend that was removed. */}
        <div className="ml-auto flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <GanttMilestoneLegend isHidden={isMsHidden} onToggle={toggleMs} testIdPrefix="labour-legend" />
        </div>
      </div>

      {/* Gantt body — ONE page scroll: the header band pins to the page, the
          rows scroll with it, the timeline scrolls sideways only. */}
      <div ref={bodyRef} data-testid="labour-gantt-body" className="relative">
      {/* 📌 The header band — OUTSIDE the timeline (a sticky child of an x-only
          scroller is inert), opaque, `GANTT_AXIS_Z`, pinned at the measured
          `stickyTop`. Its clip follows the timeline; a drag or `deltaX` wheel
          on it pans the timeline. */}
      <div
        ref={dateBandRef}
        data-testid="labour-gantt-date-band"
        className={`sticky top-0 ${GANTT_AXIS_Z} flex cursor-grab bg-white dark:bg-gray-800`}
        style={{ top: stickyTop }}
        onWheel={handleBandWheel}
      >
        <div
          className="flex-shrink-0 border-r border-b border-gray-200 dark:border-gray-700"
          style={{ width: LABEL_WIDTH, height: GANTT_HEADER_HEIGHT }}
        />
        <div ref={dateAxisClipRef} data-testid="labour-gantt-date-band-clip" className="flex-1 min-w-0 overflow-hidden" style={timelineFadeStyle}>
          <GanttDateAxis
            testId="labour-gantt-date-axis"
            className="relative"
            style={{ width: totalDays * dayWidth, minWidth: '100%' }}
            dateHeaders={dateHeaders}
            dayWidth={dayWidth}
          />
        </div>
      </div>

      <div className="flex relative">
        {/* The inline panel, over the gap under its row (see the block above
            the component). `data-pan-skip`: a drag inside it is the panel's,
            never a pan of the chart. */}
        {panelOpen && panelTop != null && (
          <div
            ref={panelRef}
            data-testid="labour-inline-panel"
            data-pan-skip=""
            /* Bare `bg-gray-50`: index.css's `html.dark .bg-gray-50 { … !important }`
               supplies the dark fill (opaque), so a `dark:` pair would be inert. */
            className="absolute inset-x-0 p-3 bg-gray-50 border-b border-gray-200 dark:border-gray-600 cursor-auto"
            style={{ top: panelTop, zIndex: GANTT_PANEL_Z }}
          >
            {panel}
          </div>
        )}
        {/* Left: person names */}
        <div ref={labelsRef} className="flex-shrink-0 border-r border-gray-200 dark:border-gray-700" style={{ width: LABEL_WIDTH }}>
          {personRows.map(p => (
            <React.Fragment key={p.user_id}>
            <div
              data-labour-row={p.user_id}
              data-testid={p.unowned ? 'labour-unowned-row' : undefined}
              title={p.unowned ? 'Gags with no owner. Open a gag to give it one.' : undefined}
              className={`flex items-center px-3 border-b border-gray-200 dark:border-gray-600 ${p.hasOverlap ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}`}
              style={{ height: p.height }}>
              <div className="flex items-center gap-2 min-w-0">
                {/* Not a person: a dashed, empty disc in the "nobody" red, where a
                    person's initial sits. The label beside it carries the meaning. */}
                {p.unowned ? (
                  <span aria-hidden="true" className="w-7 h-7 rounded-full border-2 border-dashed border-red-400 dark:border-red-500 flex-shrink-0" />
                ) : (
                <span className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs font-bold flex items-center justify-center flex-shrink-0">
                  {p.display_name?.charAt(0)?.toUpperCase() || '?'}
                </span>
                )}
                {/* 🔀 `text-xs` → `text-sm` on 2026-08-08. Product owner, after
                    asking whether the Breakdown's Timeline and Labour views used
                    the same type: *"match the font to the bigger size"*.
                    He was right that they differed — this row label was 12px
                    against `GagTimelineView.js:480`'s 14px for the equivalent
                    left-column row label, so the persistent column your eye runs
                    down was 2px smaller in one of the two views.

                    🔑 **Only THIS label moved.** The two other measured
                    differences are geometry-driven and were deliberately left:
                    (🔻 SUPERSEDED: both charts' bars are `GANTT_BAR_HEIGHT`
                    (22px since 2026-10-13) with a `text-xs` label.)
                    the bar label is `text-[10px]` because a Labour bar is 22px
                    against Timeline's 26px (10/22 and 12/26 are the same ratio)
                    AND carries an inline `lineHeight: ${BAR_HEIGHT}px`, so 12px
                    there would sit tight against the descenders; and the day
                    header is `text-[9px]` because it renders a bare day NUMBER
                    in a `dayWidth` cell where Timeline's sublabel is a longer
                    string. Making either bigger is a GEOMETRY change (bar
                    height, and lane stacking gets taller with it), not a font
                    change, and must be scoped as one.

                    📏 **Truncation was checked, not assumed.** `LABEL_WIDTH` is
                    180; less the container's `px-3` (24), the `w-7` avatar (28)
                    and the `gap-2` (8), the name gets ~120px — ~110 when the
                    overlap ⚠ is present. At 14px Inter that is ~17 characters
                    against ~20 before, so a long name like
                    "Christopher Anderson" now clips to roughly
                    "Christopher Ander…" — full first name plus most of the
                    surname, which still identifies the person. `truncate` means
                    nothing overflows. If real names turn out to clip harder than
                    that, widening `LABEL_WIDTH` is the follow-up and is again a
                    geometry change. */}
                <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{p.display_name}</span>
              </div>
              {p.hasOverlap && (
                <span className="ml-auto text-[9px] text-amber-600 dark:text-amber-400 flex-shrink-0" title="Overlapping tasks">
                  ⚠
                </span>
              )}
            </div>
            {p.user_id === panelRowId && panelGap > 0 && <div aria-hidden="true" style={{ height: panelGap }} />}
            </React.Fragment>
          ))}
        </div>

        {/* Right: timeline */}
        <div className={`flex-1 ${PAGE_SCROLL_PANE_X} cursor-grab`} ref={scrollRef} data-testid="labour-gantt-timeline-scrollport" style={timelineFadeStyle} onScroll={readTimelineFade}>
          <div style={{ width: totalDays * dayWidth, minWidth: '100%' }}>
            {/* The date strip is in the page-pinned band above. */}
            {/* Person rows with task bars */}
            <div className="relative">
              {/* Background grid — the axis cells' own lines; weekends tinted at week zoom. */}
              <GanttGrid dateHeaders={dateHeaders} dayWidth={dayWidth} />
              <GanttTodayLine todayOffset={todayOffset} totalDays={totalDays} dayWidth={dayWidth} />

              {personRows.map(p => (
                <React.Fragment key={p.user_id}>
                <div
                  className={`relative border-b border-gray-200 dark:border-gray-600 ${p.hasOverlap ? 'bg-amber-50/30 dark:bg-amber-900/5' : ''}`}
                  style={{ height: p.height }}>
                  {/* Owner spans — a summary rail per gag OWNED, at the top of the row */}
                  {p.mySpans.map((s) => {
                    const start = parseDate(s.span_start) || parseDate(s.span_end);
                    const end = parseDate(s.span_end) || parseDate(s.span_start);
                    if (!start || !end) return null;
                    const leftDays = daysDiff(timelineStart, start);
                    const duration = Math.max(1, daysDiff(start, end) + 1);
                    const color = colors[`gag-${s.gag_id}`] || '#6366F1';
                    const top = ROW_PADDING + s._top;
                    return (
                      <div key={`owner-${s.gag_id}`}
                        className="absolute cursor-pointer"
                        data-testid={`labour-owner-${s.gag_id}`}
                        style={{ left: leftDays * dayWidth, width: duration * dayWidth, height: LEAD_LANE_HEIGHT, top }}
                        onClick={() => onTaskClick && onTaskClick({ gag_id: s.gag_id, gag_title: s.gag_title }, p.user_id)}
                        title={`${p.unowned ? 'No owner' : 'Gag owner'}: ${s.gag_title}\n${s.span_start || '?'} → ${s.span_end || '?'}`}>
                        {duration * dayWidth > 60 && (
                          <span className="text-[11px] font-medium truncate block text-gray-700 dark:text-gray-100"
                            style={{ lineHeight: `${LEAD_LABEL_HEIGHT}px` }}>
                            {p.unowned ? '' : '★ '}{s.gag_title}
                          </span>
                        )}
                        {/* The rail: a solid bracket, legs down toward the tasks it covers. */}
                        <div className="absolute inset-x-0 bottom-0 rounded-t-sm"
                          style={{ height: LEAD_RAIL_HEIGHT, borderTop: `${LEAD_RAIL_BAR}px solid ${color}`, borderLeft: `3px solid ${color}`, borderRight: `3px solid ${color}` }} />
                        {/* The gag's milestones, each centred on its day and on the rail's bar
                            (the Timeline's shapes and colours). The white halo keeps a marker
                            legible on a rail of its own hue; its own `title` names it. */}
                        {MILESTONE_DEFS.map((m) => {
                          const d = parseDate(s[m.field]);
                          if (!d || isMsHidden(m.field)) return null;
                          const centerX = daysDiff(start, d) * dayWidth + dayWidth / 2;
                          return (
                            <span key={m.field}
                              data-testid={`labour-owner-ms-${s.gag_id}-${m.field}`}
                              className={`absolute ${MILESTONE_SHAPE_CLASSES[m.shape] || MILESTONE_SHAPE_CLASSES.diamond}`}
                              style={{
                                left: centerX - MILESTONE_SIZE / 2,
                                top: LEAD_LANE_HEIGHT - LEAD_RAIL_HEIGHT + LEAD_RAIL_BAR / 2 - MILESTONE_SIZE / 2,
                                filter: 'drop-shadow(0 0 1px #fff) drop-shadow(0 0 1px #fff)',
                              }}
                              title={`${m.label}: ${formatDate(s[m.field])}\n${s.gag_title}`}>
                              <MilestoneShape shape={m.shape} />
                            </span>
                          );
                        })}
                      </div>
                    );
                  })}
                  {/* This owner's gag tasks they are NOT on: held by somebody else, or
                      by nobody (red border, and the word, so colour is not the only
                      signal). Faded, and never part of the overlap warning. */}
                  {p.mySpans.flatMap((s) => s._delegated).map((t) => {
                    const start = parseDate(t.start_date) || parseDate(t.due_date);
                    const end = parseDate(t.due_date) || parseDate(t.start_date);
                    const duration = Math.max(1, daysDiff(start, end) + 1);
                    const color = colors[colorKey(t)] || '#6B7280';
                    const unassigned = t._assignees.length === 0;
                    const holder = unassigned ? 'Unassigned: nobody is on this task' : `Assigned to ${t._assignees.join(', ')}`;
                    return (
                      <div key={`delegated-${t.id}`}
                        className="absolute rounded cursor-pointer"
                        data-testid={`labour-delegated-${p.user_id}-${t.id}`}
                        data-unassigned={unassigned ? 'true' : undefined}
                        style={{
                          left: daysDiff(timelineStart, start) * dayWidth, width: duration * dayWidth, height: BAR_HEIGHT,
                          top: ROW_PADDING + t._top,
                          ...(unassigned ? unassignedBarStyle(color) : { backgroundColor: fadedBarFill(color), border: `1px solid ${withHexAlpha(color, '80')}` }),
                        }}
                        onClick={() => onTaskClick && onTaskClick(t, p.user_id)}
                        title={`${t.title}\n${holder}\n${p.unowned ? 'This gag has no owner' : `${p.display_name} owns this gag but is not on this task`}\n${t.start_date || '?'} → ${t.due_date || '?'}\nStatus: ${t.status}`}>
                        {duration * dayWidth > 50 && (
                          <span className="text-xs px-1 truncate block text-gray-600 dark:text-gray-300"
                            style={{ lineHeight: `${BAR_HEIGHT - (unassigned ? 0 : 2)}px` }}>
                            {t.title} · {unassigned ? 'Unassigned' : t._assignees.join(', ')}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {p.datedTasks.map(t => {
                    const start = parseDate(t.start_date) || parseDate(t.due_date);
                    const end = parseDate(t.due_date) || parseDate(t.start_date);
                    const leftDays = daysDiff(timelineStart, start);
                    const duration = Math.max(1, daysDiff(start, end) + 1);
                    const personal = t.personal === true;
                    const color = personal ? PERSONAL_BAR.fill : (colors[colorKey(t)] || '#6B7280');
                    const top = ROW_PADDING + t._top;
                    // A gag task opens its gag's panel. A task with no gag opens on
                    // My Tasks — only the viewer's own: anyone else's would not be
                    // there for them, so it stays display-only.
                    const clickable = t.gag_id != null || (viewerUserId != null && p.user_id === viewerUserId);

                    return (
                      <div key={t.id}
                        className={`absolute rounded hover:opacity-80 transition-opacity ${clickable ? 'cursor-pointer' : 'cursor-default'}`}
                        data-testid={`labour-bar-${t.id}`}
                        data-personal={personal ? 'true' : undefined}
                        style={{ left: leftDays * dayWidth, width: duration * dayWidth, height: BAR_HEIGHT, top, backgroundColor: color, ...(personal ? { border: `1px solid ${PERSONAL_BAR.border}` } : null) }}
                        onClick={() => clickable && onTaskClick && onTaskClick(t, p.user_id)}
                        title={`${t.title}\n${colorLabel(t)}\n${t.start_date || '?'} → ${t.due_date || '?'}\nStatus: ${t.status}`}>
                        {duration * dayWidth > 50 ? (
                          <span className={`text-xs font-medium px-1 truncate block ${personal ? '' : 'text-white'}`}
                            style={personal
                              ? { lineHeight: `${BAR_HEIGHT - 2}px`, color: PERSONAL_BAR.text }
                              : { lineHeight: `${BAR_HEIGHT}px`, textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                            <span className="mr-0.5" style={{ color: personal ? PERSONAL_BAR.text : TASK_STATUS_ICON_COLORS[t.status] }}>{TASK_STATUS_ICONS[t.status] || ''}</span>{t.title}
                          </span>
                        ) : duration * dayWidth >= 16 && (
                          <span className="text-[10px] flex items-center justify-center" style={personal
                            ? { lineHeight: `${BAR_HEIGHT - 2}px`, color: PERSONAL_BAR.text }
                            : { lineHeight: `${BAR_HEIGHT}px`, textShadow: '0 1px 2px rgba(0,0,0,0.3)', color: TASK_STATUS_ICON_COLORS[t.status] || '#fff' }}>
                            {TASK_STATUS_ICONS[t.status] || ''}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {p.user_id === panelRowId && panelGap > 0 && <div aria-hidden="true" style={{ height: panelGap }} />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* 📜 The sticky horizontal bar — only while the timeline overflows;
          `marginLeft: LABEL_WIDTH` makes its range the timeline's. */}
      {timelineOverflows ? (
        <StickyScrollbar
          ref={stickyBarRef}
          contentStyle={{ width: timelineScrollWidth }}
          testId="labour-gantt-scrollbar"
          style={{ marginLeft: LABEL_WIDTH }}
        />
      ) : null}
    </div>
  );
}
